import { apiClient, ensureCsrfCookie } from '../../api/client';

/**
 * «Стол» (stl1) — разговор владельца с живой сессией Claude Code.
 * Бэкенд: /api/admin/stol/* (только право stol.access — владелец).
 * Живое — опросом, не потоком (закон php-fpm пяти процессов).
 */

export interface StolRoomUsage {
  input_tokens?: number;
  output_tokens?: number;
  cache_creation_input_tokens?: number;
  cache_read_input_tokens?: number;
  responses?: number;
  /** Остаток подписочного окна (из total_tokens_reminder транскрипта). */
  tokens_left?: number;
  /** База окна (максимум виденного остатка) — для честного «использовано ~N%». */
  tokens_window?: number;
}

export interface StolRoom {
  id: number;
  name: string;
  model: string | null;
  /** s964: who works in the session and its role from the panel. */
  provider: 'claude' | 'codex';
  role: StolRole | null;
  stopping: boolean;
  status: 'creating' | 'alive' | 'gone' | 'failed';
  origin: 'panel' | 'terminal';
  usage: StolRoomUsage | null;
  last_seen_at: string | null;
  last_error: string | null;
}

export interface StolMessage {
  id: number;
  direction: 'owner' | 'code' | 'system';
  kind: 'text' | 'tool' | 'snapshot' | 'notice';
  content: string;
  meta: Record<string, unknown> | null;
  status: 'queued' | 'sent' | 'delivered' | 'failed';
  created_at: string | null;
}

export type StolRole = 'shtab' | 'koordinator' | 'ispolnitel' | 'svobodnaya';

export interface RoomsResponse {
  bridge_alive: boolean;
  rooms: StolRoom[];
  /** Session that receives executors' reports now, or null. */
  coordinator?: string | null;
  words?: Record<string, string>;
}

export interface MessagesResponse {
  room: { id: number; name: string; status: StolRoom['status']; usage: StolRoomUsage | null };
  bridge_alive: boolean;
  messages: StolMessage[];
}

export async function fetchRooms(): Promise<RoomsResponse> {
  const { data } = await apiClient.get('/admin/stol/rooms', { baseURL: rootApi() });
  return data as RoomsResponse;
}

export async function createRoom(name: string, model: string): Promise<StolRoom> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post('/admin/stol/rooms', { name, model }, { baseURL: rootApi() });
  return (data as { room: StolRoom }).room;
}

export async function startSession(role: StolRole, provider: StolRoom['provider'], task: string): Promise<StolRoom> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post('/admin/stol/sessions', { role, provider, task }, { baseURL: rootApi() });
  return (data as { room: StolRoom }).room;
}

export async function stopRoom(roomId: number): Promise<void> {
  await ensureCsrfCookie();
  await apiClient.post(`/admin/stol/rooms/${roomId}/stop`, {}, { baseURL: rootApi() });
}

export async function setCoordinator(roomId: number, on: boolean): Promise<void> {
  await ensureCsrfCookie();
  await apiClient.post(`/admin/stol/rooms/${roomId}/coordinator`, { on }, { baseURL: rootApi() });
}

export async function fetchMessages(roomId: number, afterId: number): Promise<MessagesResponse> {
  const { data } = await apiClient.get(`/admin/stol/rooms/${roomId}/messages`, {
    params: afterId > 0 ? { after_id: afterId } : {},
    baseURL: rootApi(),
  });
  return data as MessagesResponse;
}

export async function sendMessage(roomId: number, text: string): Promise<StolMessage> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post(
    `/admin/stol/rooms/${roomId}/messages`,
    { text },
    { baseURL: rootApi() },
  );
  return (data as { message: StolMessage }).message;
}

/** `s979`: фото или голос владельца — сессии уходит путь к файлу или расшифровка. */
export async function sendAttachment(roomId: number, kind: 'photo' | 'voice', file: Blob, name: string, text: string): Promise<StolMessage> {
  await ensureCsrfCookie();
  const form = new FormData();
  form.append('kind', kind);
  form.append('file', file, name);
  if (text !== '') form.append('text', text);
  const { data } = await apiClient.post(`/admin/stol/rooms/${roomId}/attachments`, form, {
    baseURL: rootApi(),
    headers: { 'Content-Type': 'multipart/form-data' },
    // голос расшифровывается на сервере в том же запросе
    timeout: 180000,
  });
  return (data as { message: StolMessage }).message;
}

export interface SnapshotPayload {
  image: Blob;
  url: string;
  title: string;
  /** JSON контракта состояния экрана; null — раздел не паспортизирован. */
  passport: string | null;
}

export async function sendSnapshot(roomId: number, payload: SnapshotPayload): Promise<StolMessage> {
  await ensureCsrfCookie();
  const form = new FormData();
  form.append('image', payload.image, 'screen.png');
  form.append('url', payload.url);
  form.append('title', payload.title);
  if (payload.passport !== null) {
    form.append('passport', payload.passport);
  }
  const { data } = await apiClient.post(`/admin/stol/rooms/${roomId}/snapshot`, form, {
    baseURL: rootApi(),
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return (data as { message: StolMessage }).message;
}

/** Роуты Стола живут под /api/admin/*, а не /api/v1/* — база без версии. */
function rootApi(): string {
  const base = (apiClient.defaults.baseURL ?? 'https://api.opsmind.ru/api/v1').replace(/\/$/, '');
  return base.replace(/\/api\/v1$/, '/api');
}
